import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useZeitwahl } from '../provider';
import { formatZoneLabel, formatZoneOffset } from '../time/format';
import { listTimeZones, searchTimeZones } from '../time/zones';

export interface TimeZoneSelectProps {
  value: string;
  onChange: (timeZone: string) => void;
  /** Defaults to `Intl.supportedValuesOf('timeZone')`. */
  zones?: readonly string[];
  locale?: string;
  id?: string;
}

/** Search combobox over IANA zones (`role="combobox"` + `listbox`, arrow keys, Enter, Escape). */
export function TimeZoneSelect(props: TimeZoneSelectProps): ReactNode {
  const { value, onChange, zones } = props;
  const { t, locale: contextLocale } = useZeitwahl();
  const locale = props.locale ?? contextLocale;
  const baseId = useId();
  const inputId = props.id ?? `${baseId}-tz`;
  const listId = `${baseId}-list`;
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const allZones = useMemo(() => {
    const list = zones ?? listTimeZones();
    return list.includes(value) ? list : [value, ...list];
  }, [zones, value]);
  const matches = useMemo(
    () => (open ? searchTimeZones(query, allZones, 500) : []),
    [open, query, allZones]
  );
  const activeIndex = Math.max(0, Math.min(active, matches.length - 1));
  const activeId = open && matches[activeIndex] ? `${listId}-${activeIndex}` : undefined;

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, activeIndex]);

  const openList = () => {
    if (open) return;
    setOpen(true);
    setQuery('');
    setActive(Math.max(0, allZones.indexOf(value)));
  };
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const select = (tz: string) => {
    onChange(tz);
    close();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        openList();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive(Math.min(activeIndex + 1, matches.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive(Math.max(activeIndex - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const tz = matches[activeIndex];
        if (tz) select(tz);
        break;
      }
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  return (
    <div className="zw-tz">
      <label className="zw-label" htmlFor={inputId}>
        {t('timeZoneLabel')}
      </label>
      <div className="zw-tz-control">
        <input
          id={inputId}
          className="zw-input zw-tz-input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          autoComplete="off"
          spellCheck={false}
          placeholder={t('timeZoneSearch')}
          value={open ? query : formatZoneLabel(value, locale)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            if (!open) setOpen(true);
          }}
          onFocus={openList}
          onClick={openList}
          onKeyDown={onKeyDown}
          onBlur={close}
        />
        {open && (
          <ul
            id={listId}
            ref={listRef}
            role="listbox"
            className="zw-tz-list"
            aria-label={t('timeZoneLabel')}
            onMouseDown={(e) => e.preventDefault()}
          >
            {matches.length === 0 ? (
              <li className="zw-tz-empty zw-muted" role="presentation">
                {t('timeZoneNoResults')}
              </li>
            ) : (
              matches.map((tz, i) => (
                <li
                  key={tz}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={tz === value}
                  data-active={i === activeIndex ? 'true' : undefined}
                  className={`zw-tz-option${i === activeIndex ? ' zw-tz-option--active' : ''}`}
                  onClick={() => select(tz)}
                  onMouseMove={() => setActive(i)}
                >
                  <span>{tz.replace(/_/g, ' ')}</span>
                  <span className="zw-tz-offset">{formatZoneOffset(tz, locale)}</span>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
